<template>
  <el-card>
    <div slot="header">
      <span>店铺管理</span>
      <div style="float: right">
        <el-button type="success" size="small" @click="openImportDialog">批量导入</el-button>
        <el-button type="primary" size="small" @click="openAddDialog">添加店铺</el-button>
      </div>
    </div>

    <!-- 搜索栏 -->
    <el-form :inline="true" :model="queryInfo" size="small" class="search-form">
      <el-form-item label="门店/项目名">
        <el-input v-model="queryInfo.keyword" placeholder="门店名或项目名" clearable
          @clear="getList(true)" @keyup.enter.native="getList(true)" style="width: 200px"></el-input>
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="queryInfo.status" placeholder="全部" clearable
          @change="getList(true)" style="width: 120px">
          <el-option label="筹建中" value="筹建中"></el-option>
          <el-option label="待运营" value="待运营"></el-option>
          <el-option label="运营中" value="运营中"></el-option>
          <el-option label="翻新中" value="翻新中"></el-option>
          <el-option label="停业中" value="停业中"></el-option>
          <el-option label="春节停业" value="春节停业"></el-option>
          <el-option label="闭店" value="闭店"></el-option>
          <!-- 以下为生命周期终态（isActive:false）：解约=未释放商圈名额；关店=已在门店地图释放名额；服务到期=代运营自然到期 -->
          <el-option label="解约" value="解约"></el-option>
          <el-option label="关店" value="关店"></el-option>
          <el-option label="服务到期" value="服务到期"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item v-if="!myRights.isSelfOnly" label="运营师">
        <el-select v-model="queryInfo.operatorId" placeholder="全部" clearable filterable
          @change="getList(true)" style="width: 150px">
          <el-option v-for="item in operatorOptions" :key="item._id" :label="item.name" :value="item._id"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item v-if="!myRights.isSelfOnly" label="现阶段负责人">
        <el-select v-model="queryInfo.currentOperatorId" placeholder="全部" clearable filterable
          @change="getList(true)" style="width: 150px">
          <el-option v-for="item in operatorOptions" :key="item._id" :label="item.name" :value="item._id"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item label="品牌">
        <el-select v-model="queryInfo.brand" placeholder="全部" clearable
          @change="getList(true)" style="width: 120px">
          <el-option v-for="item in brandList" :key="item" :label="item" :value="item"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item label="群绑定">
        <el-select v-model="queryInfo.groupBound" placeholder="全部" clearable
          @change="getList(true)" style="width: 120px">
          <el-option label="已绑定" value="bound"></el-option>
          <el-option label="未绑定" value="unbound"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="getList(true)">查询</el-button>
        <el-button @click="resetQuery">重置</el-button>
        <el-button v-if="isAdmin" type="danger" plain icon="el-icon-delete"
          :disabled="multipleSelection.length === 0" :loading="batchDeleteLoading"
          @click="batchDelete">批量删除 ({{ multipleSelection.length }})</el-button>
      </el-form-item>
    </el-form>

    <!-- 列表（固定高度撑满内容区剩余空间，纵向/横向滚动收进表格内部） -->
    <el-table :data="list" border v-loading="loading" style="width: 100%" :height="tableHeight"
      @selection-change="handleSelectionChange">
      <el-table-column v-if="isAdmin" type="selection" width="40" :selectable="row => !isTerminatedRow(row)"></el-table-column>
      <el-table-column type="index" label="#" width="50" align="center"></el-table-column>
      <el-table-column label="企微群" width="70" align="center">
        <template slot-scope="scope">
          <el-tooltip v-if="scope.row.groupBound" :content="scope.row.groupName || '已绑定'" placement="top">
            <img :src="weworkIcon" class="wework-icon" @click="openGroupBindDialog(scope.row)" />
          </el-tooltip>
          <el-tooltip v-else :content="isTerminatedRow(scope.row) ? '未绑定（已解约/关店不可绑定）' : '未绑定'" placement="top">
            <img :src="weworkIcon" class="wework-icon"
              :class="{ 'wework-icon-unbound': !isTerminatedRow(scope.row) }"
              :style="isTerminatedRow(scope.row) ? 'opacity: 0.3; cursor: not-allowed;' : ''"
              @click="!isTerminatedRow(scope.row) && openGroupBindDialog(scope.row)" />
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column label="品牌" width="100" align="center">
        <template slot-scope="scope">
          <el-tag v-if="scope.row.brand" size="mini">{{ scope.row.brand }}</el-tag>
          <span v-else style="color: #c0c4cc">-</span>
        </template>
      </el-table-column>
      <el-table-column label="门店名称" min-width="180">
        <template slot-scope="scope">
          <div style="font-size:14px;color:#333;font-weight:600;">
            {{ scope.row.projectName }}
            <el-tag v-if="scope.row.generation > 1" size="mini" type="warning" style="margin-left: 4px">第{{ scope.row.generation }}代</el-tag>
          </div>
          <div style="color:#909399;font-size:12px;">{{ scope.row.name }}</div>
        </template>
      </el-table-column>
      <el-table-column label="状态" width="100" align="center">
        <template slot-scope="scope">
          <el-tag v-if="scope.row.status === '翻新中'" type="danger" effect="dark" size="small">翻新中</el-tag>
          <el-tag v-else :type="statusTagType(scope.row.status)" size="small">{{ scope.row.status }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="平台" min-width="200">
        <template slot-scope="scope">
          <span v-for="p in scope.row.platforms" :key="p._id">
            <el-tag size="mini" :type="p.stage === 'level2' ? 'success' : 'warning'" style="margin-right: 5px">
              {{ platformName(p.platform) }}{{ p.stage === 'level2' ? '(II)' : '(I)' }}
            </el-tag>
          </span>
          <span v-if="!scope.row.platforms || scope.row.platforms.length === 0" style="color: #c0c4cc">无</span>
        </template>
      </el-table-column>

      <el-table-column label="附加信息" width="80" align="center">
        <template slot-scope="scope">
          <el-tooltip v-if="hasAdditionalInfo(scope.row.additionalInfo)" placement="top" :open-delay="200">
            <div slot="content" class="addinfo-preview">
              <div v-for="item in additionalInfoPreview(scope.row.additionalInfo)" :key="item.key">{{ item.key }}：{{ item.value }}</div>
            </div>
            <el-button type="text" size="mini" @click="openAdditionalInfoDialog(scope.row)">查看</el-button>
          </el-tooltip>
          <span v-else style="color: #c0c4cc">--</span>
        </template>
      </el-table-column>

      <el-table-column label="现阶段负责人" width="110">
        <template slot-scope="scope">{{ scope.row.assignments.currentOperator ? scope.row.assignments.currentOperator.employeeName : '-' }}</template>
      </el-table-column>
      <el-table-column label="一阶运营" width="100">
        <template slot-scope="scope">{{ scope.row.assignments.level1Operator ? scope.row.assignments.level1Operator.employeeName : '-' }}</template>
      </el-table-column>
      <el-table-column label="二阶运营" width="100">
        <template slot-scope="scope">{{ scope.row.assignments.level2Operator ? scope.row.assignments.level2Operator.employeeName : '-' }}</template>
      </el-table-column>
      <el-table-column label="营运督导" width="100">
        <template slot-scope="scope">{{ scope.row.assignments.operatingSupervisor ? scope.row.assignments.operatingSupervisor.employeeName : '-' }}</template>
      </el-table-column>

      <el-table-column prop="targetVolume" label="目标单量" width="90" align="center"></el-table-column>
      <el-table-column label="开业时间" width="110">
        <template slot-scope="scope">{{ scope.row.openDate | dateFormat }}</template>
      </el-table-column>
      <el-table-column label="操作" width="280" fixed="right">
        <template slot-scope="scope">
          <!-- 已解约/已关店（isActive:false）为历史数据：仅可查看详情，其余操作全部禁用 -->
          <el-button type="text" size="mini" @click="openDetail(scope.row)">详情</el-button>
          <el-button type="text" size="mini" :disabled="scope.row.status === '筹建中' || isTerminatedRow(scope.row)" @click="openEditDialog(scope.row)">修改</el-button>
          <el-button v-if="scope.row.status !== '翻新中'" type="text" size="mini" :disabled="scope.row.status === '筹建中' || isTerminatedRow(scope.row)" @click="openOperatorDialog(scope.row)">指定负责人</el-button>
          <el-button type="text" size="mini" :disabled="scope.row.status === '筹建中' || isTerminatedRow(scope.row)" @click="openStatusDialog(scope.row)">改状态</el-button>
          <el-button v-if="canRenovate(scope.row)" type="text" size="mini" style="color: #e6a23c; font-weight: 600" @click="openRenovateDialog(scope.row)">翻新</el-button>
          <el-button v-if="isBrandRow(scope.row)" type="text" size="mini" style="color: #f56c6c" :disabled="scope.row.status === '筹建中' || scope.row.status === '翻新中' || isTerminatedRow(scope.row)" @click="openPermanentCloseDialog(scope.row)">永久关闭</el-button>
          <el-button type="text" size="mini" style="color: #f56c6c" v-if="isAdmin" :disabled="scope.row.status === '筹建中' || scope.row.generation > 1 || isTerminatedRow(scope.row)" @click="handleRemove(scope.row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <el-pagination
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
      :current-page="queryInfo.pagenum"
      :page-sizes="[10, 20, 50]"
      :page-size="queryInfo.pagesize"
      :total="total"
      layout="total, sizes, prev, pager, next, jumper"
      style="margin-top: 15px; text-align: right"
    ></el-pagination>

    <!-- 添加弹窗 -->
    <el-dialog title="添加店铺" :visible.sync="addDialog.visible" width="800px" top="5vh" :close-on-click-modal="false">
      <el-form :model="addForm" label-width="110px" size="small" ref="addFormRef">
        <!-- 项目选择 -->
        <el-form-item label="项目" required>
          <el-select
            v-model="addForm.projectId"
            placeholder="搜索项目名称"
            filterable remote :remote-method="searchProjects"
            :loading="addDialog.projectLoading"
            @change="onProjectChange"
            style="width: 100%">
            <el-option v-for="item in projectOptions" :key="item._id"
              :label="`${item.projectName}（${item.customerName}）`" :value="item._id"></el-option>
          </el-select>
        </el-form-item>

        <!-- 门店状态 / 店铺单量（同一行左右分布） -->
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="门店状态" required>
              <el-select v-model="addForm.status" style="width: 100%">
                <el-option label="待运营" value="待运营"></el-option>
                <el-option label="运营中" value="运营中"></el-option>
                <el-option label="停业中" value="停业中"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="店铺单量" required>
              <el-input-number v-model="addForm.targetVolume" :min="0" style="width: 100%"></el-input-number>
            </el-form-item>
          </el-col>
        </el-row>

        <el-divider content-position="left">平台账号</el-divider>

        <!-- 平台列表 -->
        <div v-for="(p, index) in addForm.platforms" :key="p.platform" style="margin-bottom: 10px">
          <el-card shadow="never" style="background: #f9f9f9">
            <el-form-item style="margin-bottom: 10px">
              <template slot="label"><img :src="platformIcons[p.platform]" class="platform-icon" /></template>
              <el-row :gutter="10">
                <el-col :span="6">
                  <el-input v-model="p.code" placeholder="平台编码" size="mini"></el-input>
                </el-col>
                <el-col :span="10">
                  <el-input v-model="p.storeName" placeholder="平台店名" size="mini"></el-input>
                </el-col>
              </el-row>
              <el-row :gutter="10" style="margin-top: 8px">
                <el-col :span="6">
                  <el-date-picker v-model="p.openDate" type="date" placeholder="开业时间"
                    value-format="yyyy-MM-dd" format="yyyy-MM-dd" size="mini" style="width: 100%"></el-date-picker>
                </el-col>
                <el-col :span="5">
                  <el-input v-model="p.targetVolume" placeholder="平台单量" size="mini" type="number"></el-input>
                </el-col>
                <el-col :span="5">
                  <el-select v-model="p.stage" placeholder="运营阶段" size="mini" @change="onStageChange(index)">
                    <el-option label="一阶" value="level1"></el-option>
                    <el-option label="二阶" value="level2"></el-option>
                  </el-select>
                </el-col>
                <el-col :span="6" v-if="p.stage === 'level2'">
                  <el-date-picker v-model="p.level2StartedAt" type="date" placeholder="转二阶日期"
                    value-format="yyyy-MM-dd" format="yyyy-MM-dd" size="mini" style="width: 100%"></el-date-picker>
                </el-col>
              </el-row>
            </el-form-item>
          </el-card>
        </div>

        <el-divider content-position="left">负责人</el-divider>

        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="一阶运营师">
              <el-select v-model="addForm.level1OperatorId" placeholder="选择" filterable clearable size="small" style="width: 100%">
                <el-option v-for="item in operatorOptions" :key="item._id" :label="item.name" :value="item._id"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="营运督导">
              <el-select v-model="addForm.operatingSupervisorId" placeholder="选择" filterable clearable size="small" style="width: 100%">
                <el-option v-for="item in supervisorOptions" :key="item._id" :label="item.name" :value="item._id"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="二阶运营师">
              <el-select v-model="addForm.level2OperatorId" placeholder="选择" filterable clearable size="small"
                :disabled="!hasLevel2Platform" style="width: 100%">
                <el-option v-for="item in operatorOptions" :key="item._id" :label="item.name" :value="item._id"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="现阶段负责人">
              <el-select v-model="addForm.currentOperatorId" placeholder="选择" filterable clearable size="small" style="width: 100%">
                <el-option v-for="item in operatorOptions" :key="item._id" :label="item.name" :value="item._id"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>

      <div slot="footer">
        <el-button @click="addDialog.visible = false">取消</el-button>
        <el-button type="primary" :loading="addDialog.submitting" @click="confirmAdd">确认添加</el-button>
      </div>
    </el-dialog>

    <!-- 修改弹窗（复用添加弹窗结构，锁定项目/状态/营运督导） -->
    <el-dialog title="修改店铺" :visible.sync="editDialog.visible" width="800px" top="5vh" :close-on-click-modal="false">
      <el-form :model="editForm" label-width="110px" size="small">
        <!-- 项目（只读） / 门店名称（可编辑） -->
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="项目">
              <el-input :value="editForm.projectName" disabled style="width: 100%"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="门店名称" required>
              <el-input v-model="editForm.name" placeholder="请输入门店名称" maxlength="50" show-word-limit style="width: 100%"></el-input>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 门店状态（只读，走"改状态"） / 店铺单量 -->
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="门店状态">
              <el-input :value="editForm.status" disabled style="width: 100%"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="店铺单量" required>
              <el-input-number v-model="editForm.targetVolume" :min="0" style="width: 100%"></el-input-number>
            </el-form-item>
          </el-col>
        </el-row>

        <el-divider content-position="left">平台账号</el-divider>

        <el-alert v-if="editForm.status === '翻新中' || editForm.generation > 1" type="warning" :closable="false" style="margin-bottom: 10px">
          本店为翻新产生的代：填写历史各代未出现过的平台编码将被标记为"翻新换号"；沿用历史编码会自动保留其历史开业时间。{{ editForm.status === '翻新中' ? '翻新中的新平台开业时间留空，待"设置开业"时填写。' : '' }}
        </el-alert>

        <div v-for="(p, index) in editForm.platforms" :key="p.platform" style="margin-bottom: 10px">
          <el-card shadow="never" style="background: #f9f9f9">
            <el-form-item style="margin-bottom: 10px">
              <template slot="label"><img :src="platformIcons[p.platform]" class="platform-icon" /></template>
              <el-row :gutter="10">
                <el-col :span="6">
                  <el-input v-model="p.code" placeholder="平台编码（留空则删除）" size="mini"></el-input>
                </el-col>
                <el-col :span="10">
                  <el-input v-model="p.storeName" placeholder="平台店名" size="mini"></el-input>
                </el-col>
              </el-row>
              <el-row :gutter="10" style="margin-top: 8px">
                <el-col :span="6">
                  <el-date-picker v-model="p.openDate" type="date" placeholder="开业时间"
                    value-format="yyyy-MM-dd" format="yyyy-MM-dd" size="mini" style="width: 100%"></el-date-picker>
                </el-col>
                <el-col :span="5">
                  <el-input v-model="p.targetVolume" placeholder="平台单量" size="mini" type="number"></el-input>
                </el-col>
                <el-col :span="5">
                  <el-select v-model="p.stage" placeholder="运营阶段" size="mini" @change="onEditStageChange(index)">
                    <el-option label="一阶" value="level1"></el-option>
                    <el-option label="二阶" value="level2"></el-option>
                  </el-select>
                </el-col>
                <el-col :span="6" v-if="p.stage === 'level2'">
                  <el-date-picker v-model="p.level2StartedAt" type="date" placeholder="转二阶日期"
                    value-format="yyyy-MM-dd" format="yyyy-MM-dd" size="mini" style="width: 100%"></el-date-picker>
                </el-col>
              </el-row>
            </el-form-item>
          </el-card>
        </div>

        <el-divider content-position="left">负责人</el-divider>

        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="一阶运营师">
              <el-select v-model="editForm.level1OperatorId" placeholder="选择" filterable clearable size="small" style="width: 100%">
                <el-option v-for="item in operatorOptions" :key="item._id" :label="item.name" :value="item._id"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="二阶运营师">
              <el-select v-model="editForm.level2OperatorId" placeholder="选择" filterable clearable size="small"
                :disabled="!editHasLevel2Platform" style="width: 100%">
                <el-option v-for="item in operatorOptions" :key="item._id" :label="item.name" :value="item._id"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="现阶段负责人">
              <el-select v-model="editForm.currentOperatorId" placeholder="选择" filterable clearable size="small" style="width: 100%">
                <el-option v-for="item in operatorOptions" :key="item._id" :label="item.name" :value="item._id"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <div style="color: #909399; font-size: 12px; margin-top: -5px">注：营运督导由督导部门维护，此处不可修改</div>

        <template v-if="editForm.status === '翻新中' || editForm.generation > 1">
          <el-divider content-position="left">翻新执照（留存）</el-divider>
          <el-row :gutter="20" class="license-upload-row">
            <el-col :span="12">
              <el-form-item label="营业执照">
                <image-upload v-model="editForm.licenseImages.businessLicense" :limit="1" :show-tip="false" :active="editDialog.visible" />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="餐饮许可证">
                <image-upload v-model="editForm.licenseImages.foodPermit" :limit="1" :show-tip="false" :active="editDialog.visible" />
              </el-form-item>
            </el-col>
          </el-row>
          <div style="color: #909399; font-size: 12px; margin-top: -5px">注：执照随"确认修改"一并保存，仅作留存，不作为设置开业的强制条件</div>
        </template>
      </el-form>

      <div slot="footer">
        <el-button @click="editDialog.visible = false">取消</el-button>
        <el-button type="primary" :loading="editDialog.submitting" @click="confirmEdit">确认修改</el-button>
      </div>
    </el-dialog>

    <!-- 修改状态弹窗 -->
    <el-dialog :title="isRenovatingStatus ? '设置开业（翻新完成）' : '修改状态'" :visible.sync="statusDialog.visible" width="500px">
      <el-form label-width="80px" size="small">
        <el-form-item label="门店">
          <span>{{ statusDialog.row ? statusDialog.row.name : '' }}</span>
        </el-form-item>
        <el-form-item label="当前状态">
          <span>{{ statusDialog.row ? statusDialog.row.status : '' }}</span>
        </el-form-item>
        <el-form-item label="新状态" required>
          <el-select v-if="isRenovatingStatus" v-model="statusDialog.status" style="width: 200px">
            <el-option label="运营中（设置开业）" value="运营中"></el-option>
          </el-select>
          <el-select v-else v-model="statusDialog.status" placeholder="选择" style="width: 200px">
            <!-- 代运营门店仅允许运营级状态；服务到期请在代运营管理中设置（方案 2026-10-12 §3.2.5） -->
            <template v-if="isProxyRow(statusDialog.row)">
              <el-option label="运营中" value="运营中"></el-option>
              <el-option label="停业中" value="停业中"></el-option>
              <el-option label="春节停业" value="春节停业"></el-option>
            </template>
            <template v-else>
              <el-option label="待运营" value="待运营"></el-option>
              <el-option label="运营中" value="运营中"></el-option>
              <el-option label="停业中" value="停业中"></el-option>
              <el-option label="春节停业" value="春节停业"></el-option>
            </template>
          </el-select>
        </el-form-item>
        <el-form-item v-if="!isRenovatingStatus && isProxyRow(statusDialog.row)" label-width="0">
          <el-alert type="info" :closable="false">服务到期请在【运营管理 → 代运营管理】中标记</el-alert>
        </el-form-item>
        <el-form-item v-if="isRenovatingStatus && statusDialog.pendingPlatforms.length > 0" label="开业时间" required>
          <div style="color: #909399; font-size: 12px; margin-bottom: 6px">以下平台尚未填写开业时间，请补充：</div>
          <div v-for="p in statusDialog.pendingPlatforms" :key="p.platform" style="display: flex; align-items: center; margin-bottom: 6px">
            <img :src="platformIcons[p.platform]" class="platform-icon" style="margin-right: 6px" />
            <span style="width: 90px; margin-right: 8px">{{ platformName(p.platform) }}{{ p.isRenovated ? '（换号）' : '' }}</span>
            <el-date-picker v-model="statusDialog.platformOpenDates[p.platform]" type="date" placeholder="开业时间"
              value-format="yyyy-MM-dd" format="yyyy-MM-dd" size="mini" style="width: 160px"></el-date-picker>
          </div>
        </el-form-item>
        <el-form-item label="理由" required>
          <el-input type="textarea" v-model="statusDialog.reason" :rows="3" placeholder="请填写修改理由"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="statusDialog.visible = false">取消</el-button>
        <el-button type="primary" :loading="statusDialog.submitting" @click="confirmStatus">确认</el-button>
      </div>
    </el-dialog>

    <!-- 翻新确认弹窗 -->
    <el-dialog title="门店翻新" :visible.sync="renovateDialog.visible" width="560px" :close-on-click-modal="false">
      <el-alert type="warning" :closable="false" style="margin-bottom: 15px">
        <div>确认翻新将执行以下操作（不可撤销）：</div>
        <div>1. 关闭当前门店（第{{ renovateDialog.generation }}代，状态变为"已翻新"，历史数据保留可查）</div>
        <div>2. 新建第{{ renovateDialog.generation + 1 }}代门店，状态为"翻新中"</div>
        <div>3. 各平台账号默认沿用；需换号的平台，请在翻新中通过"修改"更新编码</div>
        <div>4. 负责人不变、企微群不变</div>
      </el-alert>
      <el-form label-width="80px" size="small">
        <el-form-item label="门店">
          <span>{{ renovateDialog.row ? renovateDialog.row.projectName : '' }}</span>
        </el-form-item>
        <el-form-item label="翻新原因">
          <el-input type="textarea" v-model="renovateDialog.reason" :rows="3" placeholder="选填，留存翻新原因"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="renovateDialog.visible = false">取消</el-button>
        <el-button type="danger" :loading="renovateDialog.submitting" @click="confirmRenovate">确认翻新</el-button>
      </div>
    </el-dialog>

    <!-- 永久关闭弹窗（品牌终态：闭店） -->
    <el-dialog title="永久关闭（闭店）" :visible.sync="permanentCloseDialog.visible" width="520px" :close-on-click-modal="false">
      <el-alert type="warning" :closable="false" style="margin-bottom: 15px">
        <div>永久关闭将执行以下操作（不可撤销）：</div>
        <div>1. 门店转为终态"闭店"，各平台停用、负责人收回</div>
        <div>2. 历史数据保留可查</div>
        <div>3. 客户重新合作请走新项目/新签合同流程，本门店不再复活</div>
      </el-alert>
      <el-form label-width="80px" size="small">
        <el-form-item label="门店">
          <span>{{ permanentCloseDialog.row ? permanentCloseDialog.row.name : '' }}</span>
        </el-form-item>
        <el-form-item label="关闭原因" required>
          <el-input type="textarea" v-model="permanentCloseDialog.reason" :rows="3" placeholder="请填写关闭原因（如客户不再开店）"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="permanentCloseDialog.visible = false">取消</el-button>
        <el-button type="danger" :loading="permanentCloseDialog.submitting" @click="confirmPermanentClose">确认永久关闭</el-button>
      </div>
    </el-dialog>

    <!-- 指定负责人弹窗 -->
    <el-dialog title="指定现阶段负责人" :visible.sync="operatorDialog.visible" width="450px">
      <el-form label-width="110px" size="small">
        <el-form-item label="门店">
          <span>{{ operatorDialog.row ? operatorDialog.row.name : '' }}</span>
        </el-form-item>
        <el-form-item label="当前负责人">
          <span>{{ operatorDialog.currentName }}</span>
        </el-form-item>
        <el-form-item label="新负责人" required>
          <el-select v-model="operatorDialog.employeeId" placeholder="选择运营部员工" filterable clearable style="width: 100%">
            <el-option v-for="item in operatorOptions" :key="item._id" :label="item.name" :value="item._id"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="理由" required>
          <el-input type="textarea" v-model="operatorDialog.reason" :rows="3" placeholder="请填写指定/变更理由"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="operatorDialog.visible = false">取消</el-button>
        <el-button type="primary" :loading="operatorDialog.submitting" @click="confirmOperator">确认</el-button>
      </div>
    </el-dialog>

    <!-- 详情弹窗 -->
    <el-dialog title="店铺详情" :visible.sync="detailDialog.visible" width="960px" top="5vh">
      <div v-if="detailData">
        <el-descriptions :column="2" border size="small">
          <el-descriptions-item label="门店名称">{{ detailData.name }}</el-descriptions-item>
          <el-descriptions-item label="所属项目">{{ detailData.projectName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag v-if="detailData.status === '翻新中'" type="danger" effect="dark" size="small">翻新中</el-tag>
            <span v-else>{{ detailData.status }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="代际">第{{ detailData.generation || 1 }}代{{ detailData.generations && detailData.generations.length > 1 ? `（共${detailData.generations.length}代）` : '' }}</el-descriptions-item>
          <el-descriptions-item label="目标单量">{{ detailData.targetVolume }}</el-descriptions-item>
          <el-descriptions-item label="最早开业时间">{{ detailData.openDate | dateFormat }}</el-descriptions-item>
          <el-descriptions-item label="现阶段负责人">{{ currentOperatorName(detailData.assignments) }}</el-descriptions-item>
          <el-descriptions-item label="一阶运营师">{{ operatorName(detailData.assignments, 'level1Operator') }}</el-descriptions-item>
          <el-descriptions-item label="二阶运营师">{{ operatorName(detailData.assignments, 'level2Operator') }}</el-descriptions-item>
        </el-descriptions>

        <!-- 多代项目：按代 tab 展示各代平台账号 -->
        <template v-if="detailData.generations && detailData.generations.length">
          <h4 style="margin: 15px 0 8px">各代经营记录</h4>
          <el-tabs v-model="detailDialog.activeGen" type="card">
            <el-tab-pane v-for="gen in detailData.generations" :key="gen._id" :name="gen._id">
              <span slot="label">第{{ gen.generation }}代{{ gen.isActive ? '·当前' : '' }}</span>
              <el-descriptions :column="3" border size="mini" style="margin-bottom: 10px">
                <el-descriptions-item label="状态">{{ gen.status }}</el-descriptions-item>
                <el-descriptions-item label="结束原因">{{ closeReasonLabel(gen.closeReason) }}</el-descriptions-item>
                <el-descriptions-item :label="gen.closedAt ? '结束时间' : '最早开业'">{{ (gen.closedAt || gen.openDate) | dateFormat }}</el-descriptions-item>
                <el-descriptions-item label="负责人" :span="3">{{ genOperatorsText(gen.assignments) }}</el-descriptions-item>
              </el-descriptions>
              <el-table :data="gen.platforms" border size="mini">
                <el-table-column label="平台" width="70" align="center">
                  <template slot-scope="scope"><img :src="platformIcons[scope.row.platform]" class="platform-icon" /></template>
                </el-table-column>
                <el-table-column prop="code" label="编码" width="130"></el-table-column>
                <el-table-column label="翻新标记" width="90" align="center">
                  <template slot-scope="scope">
                    <el-tag v-if="scope.row.isRenovated" type="danger" size="mini">换号</el-tag>
                    <span v-else style="color: #909399; font-size: 12px">沿用</span>
                  </template>
                </el-table-column>
                <el-table-column prop="storeName" label="店名" min-width="110" show-overflow-tooltip></el-table-column>
                <el-table-column label="记录状态" width="80" align="center">
                  <template slot-scope="scope">{{ scope.row.status === 1 ? '在营' : '弃用' }}</template>
                </el-table-column>
                <el-table-column label="阶段" width="60" align="center">
                  <template slot-scope="scope">{{ scope.row.stage === 'level2' ? '二阶' : '一阶' }}</template>
                </el-table-column>
                <el-table-column label="开业时间" width="100">
                  <template slot-scope="scope">{{ scope.row.openDate | dateFormat }}</template>
                </el-table-column>
              </el-table>

              <!-- 翻新执照（跟随代：留存于本次翻新产生的那一代） -->
              <template v-if="gen.renovationLicenses && gen.renovationLicenses.length > 0">
                <h4 style="margin: 12px 0 8px">翻新执照</h4>
                <div v-for="(l, i) in gen.renovationLicenses" :key="i" style="display: inline-block; margin-right: 12px">
                  <el-image :src="base + l.fileUrl" :preview-src-list="gen.renovationLicenses.map(x => base + x.fileUrl)"
                    style="width: 80px; height: 80px; border-radius: 4px" fit="cover"></el-image>
                  <div style="font-size: 12px; color: #909399; text-align: center">{{ l.type === 'businessLicense' ? '营业执照' : '餐饮许可证' }}</div>
                </div>
              </template>
            </el-tab-pane>
          </el-tabs>
        </template>

        <!-- 单代：原有平台信息表 -->
        <template v-else>
          <h4 style="margin: 15px 0 8px">平台信息</h4>
          <el-table :data="detailData.platforms" border size="mini">
            <el-table-column label="平台" width="80">
              <template slot-scope="scope"><img :src="platformIcons[scope.row.platform]" class="platform-icon" /></template>
            </el-table-column>
            <el-table-column prop="code" label="编码" width="120"></el-table-column>
            <el-table-column prop="storeName" label="店名" min-width="120"></el-table-column>
            <el-table-column label="阶段" width="80">
              <template slot-scope="scope">{{ scope.row.stage === 'level2' ? '二阶' : '一阶' }}</template>
            </el-table-column>
            <el-table-column label="目标单量" width="90" align="center">
              <template slot-scope="scope">{{ scope.row.targetVolume != null ? scope.row.targetVolume : '-' }}</template>
            </el-table-column>
            <el-table-column label="开业时间" width="110">
              <template slot-scope="scope">{{ scope.row.openDate | dateFormat }}</template>
            </el-table-column>
            <el-table-column label="转二阶" width="110">
              <template slot-scope="scope">{{ scope.row.level2StartedAt | dateFormat }}</template>
            </el-table-column>
          </el-table>
        </template>

        <h4 style="margin: 15px 0 8px">操作日志</h4>
        <el-table :data="detailData.operationLogs" border size="mini" style="margin-bottom: 15px">
          <el-table-column label="操作" width="140">
            <template slot-scope="scope">{{ actionLabel(scope.row.action) }}</template>
          </el-table-column>
          <el-table-column prop="operatorName" label="操作人" width="100"></el-table-column>
          <el-table-column prop="remark" label="说明" min-width="150"></el-table-column>
          <el-table-column label="时间" width="150">
            <template slot-scope="scope">{{ scope.row.createDate | timeFormat }}</template>
          </el-table-column>
        </el-table>
      </div>
    </el-dialog>

    <!-- 批量导入弹窗 -->
    <el-dialog title="批量导入" :visible.sync="importDialog.visible" width="1000px" top="5vh" :close-on-click-modal="false">
      <el-steps :active="importDialog.step" finish-status="success" simple style="margin-bottom: 20px">
        <el-step title="选择项目"></el-step>
        <el-step title="下载模板"></el-step>
        <el-step title="上传导入"></el-step>
        <el-step title="结果"></el-step>
      </el-steps>

      <!-- Step 0: 选择项目（穿梭框） -->
      <div v-if="importDialog.step === 0">
        <el-transfer
          v-model="importDialog.selectedIds"
          :data="importDialog.transferData"
          :titles="['可选项目', '已选项目']"
          :props="{ key: '_id', label: 'label' }"
          filterable
          filter-placeholder="输入项目名称过滤"
          :left-default-checked="[]"
          :right-default-checked="[]"
          style="text-align: left; margin-top: 10px"
          class="import-transfer"
        ></el-transfer>
        <div style="margin-top: 10px; text-align: right">
          <el-button type="primary" :disabled="importDialog.selectedIds.length === 0"
            @click="downloadTemplate">下一步：下载模板</el-button>
        </div>
      </div>

      <!-- Step 1: 下载模板 -->
      <div v-if="importDialog.step === 1" style="text-align: center; padding: 30px">
        <p>已选择 {{ importDialog.selectedIds.length }} 个项目</p>
        <el-button type="success" :loading="importDialog.downloading" @click="doDownloadTemplate">
          下载导入模板
        </el-button>
        <p style="color: #909399; margin-top: 15px; font-size: 13px">
          请在模板中填写门店信息后，点击下一步上传
        </p>
        <div style="margin-top: 15px">
          <el-button @click="importDialog.step = 0">上一步</el-button>
          <el-button type="primary" @click="importDialog.step = 2">下一步：上传</el-button>
        </div>
      </div>

      <!-- Step 2: 上传 -->
      <div v-if="importDialog.step === 2" style="text-align: center; padding: 30px">
        <el-upload
          action=""
          :auto-upload="false"
          :on-change="handleFileChange"
          :on-remove="handleFileRemove"
          :limit="1"
          accept=".xlsx,.xls"
          drag>
          <i class="el-icon-upload"></i>
          <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
          <div class="el-upload__tip" slot="tip">只能上传 xlsx/xls 文件</div>
        </el-upload>
        <div style="margin-top: 15px">
          <el-button @click="importDialog.step = 1">上一步</el-button>
          <el-button type="primary" :disabled="!importDialog.uploadFile"
            :loading="importDialog.uploading" @click="doUpload">开始导入</el-button>
        </div>
      </div>

      <!-- Step 3: 结果 -->
      <div v-if="importDialog.step === 3">
        <el-result v-if="importDialog.result.fail === 0" icon="success"
          :title="`导入成功 ${importDialog.result.success} 条`">
        </el-result>
        <el-result v-else icon="warning"
          :title="`成功 ${importDialog.result.success} 条，失败 ${importDialog.result.fail} 条`">
        </el-result>
        <div v-if="importDialog.result.fails && importDialog.result.fails.length > 0" style="margin-top: 10px">
          <el-table :data="importDialog.result.fails" border size="mini" max-height="250">
            <el-table-column prop="row" label="行号" width="60"></el-table-column>
            <el-table-column prop="reason" label="失败原因" min-width="300" show-overflow-tooltip></el-table-column>
          </el-table>
        </div>
        <div style="margin-top: 15px; text-align: center">
          <el-button type="primary" @click="importDialog.visible = false">完成</el-button>
        </div>
      </div>
    </el-dialog>

    <!-- 企微群绑定/换绑/解绑弹窗 -->
    <el-dialog
      :title="groupBindDialog.row && groupBindDialog.row.groupBound ? '换绑企微群' : '绑定企微群'"
      :visible.sync="groupBindDialog.visible"
      width="500px"
      @close="resetGroupBindDialog"
    >
      <el-form label-width="90px" size="small">
        <el-form-item label="门店名称">
          <span>{{ groupBindDialog.row ? groupBindDialog.row.name : '' }}</span>
        </el-form-item>
        <el-form-item label="所属项目">
          <span>{{ groupBindDialog.row ? groupBindDialog.row.projectName : '' }}</span>
        </el-form-item>
        <el-form-item label="当前群" v-if="groupBindDialog.row && groupBindDialog.row.groupBound">
          <el-tag type="success" size="small">{{ groupBindDialog.row.groupName || '已绑定' }}</el-tag>
        </el-form-item>
        <el-form-item label="选择群">
          <el-select
            v-model="groupBindDialog.conversationId"
            placeholder="搜索群名称"
            filterable
            remote
            :remote-method="searchGroups"
            :loading="groupBindDialog.loading"
            style="width: 100%"
          >
            <el-option
              v-for="item in groupBindDialog.groupOptions"
              :key="item.conversation_id"
              :label="`${item.nickname}（${item.total}人${item.is_external ? '·外部' : ''}）`"
              :value="item.conversation_id"
            ></el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button
          v-if="groupBindDialog.row && groupBindDialog.row.groupBound"
          type="danger"
          plain
          style="float: left"
          :loading="groupBindDialog.unbinding"
          @click="confirmGroupUnbind"
        >解绑</el-button>
        <el-button @click="groupBindDialog.visible = false">取消</el-button>
        <el-button
          type="primary"
          :loading="groupBindDialog.submitting"
          @click="confirmGroupBind"
        >{{ groupBindDialog.row && groupBindDialog.row.groupBound ? '确认换绑' : '确认绑定' }}</el-button>
      </div>
    </el-dialog>

    <!-- 附加信息弹框（项目级键值对，只读；列表 hover 预览的完整版） -->
    <el-dialog title="附加信息" :visible.sync="additionalInfoDialog.visible" width="560px" top="8vh">
      <template v-if="additionalInfoDialog.row">
        <div class="addinfo-dialog-head">
          <span>{{ additionalInfoDialog.row.projectName }}</span>
          <span class="addinfo-dialog-sub">{{ additionalInfoDialog.row.name }}</span>
        </div>
        <el-descriptions :column="1" border size="small">
          <el-descriptions-item
            v-for="(value, key) in additionalInfoDialog.row.additionalInfo"
            :key="key"
            :label="key"
          >
            <span v-html="getMultLine(value)"></span>
          </el-descriptions-item>
        </el-descriptions>
        <div v-if="!additionalInfoDialog.row.additionalInfo || Object.keys(additionalInfoDialog.row.additionalInfo).length === 0" style="color: #909399; text-align: center; padding: 20px;">
          暂无附加信息
        </div>
      </template>
      <div slot="footer">
        <el-button @click="additionalInfoDialog.visible = false">关闭</el-button>
      </div>
    </el-dialog>
  </el-card>
</template>

<script>
import dayjs from 'dayjs'
import { brandList } from '@/utility/staticData'
import { readLevel } from '@/utility/permissions'
import ImageUpload from '@/components/ImageUpload.vue'

export default {
  name: 'YunyingDianpu',
  components: { ImageUpload },
  data () {
    return {
      // 平台图标
      platformIcons: {
        meituan: require('@/assets/meituan.png'),
        eleme: require('@/assets/eleme.png'),
        jd: require('@/assets/jd.png')
      },
      weworkIcon: require('@/assets/wework.png'),
      brandList,
      // 上传图片的访问前缀（登录时由接口 meta.base 写入，与 ChoujianDetail 等页面同规范）
      base: window.sessionStorage.getItem('base') || '',
      queryInfo: { keyword: '', status: '运营中', operatorId: '', currentOperatorId: '', groupBound: '', brand: '', pagenum: 1, pagesize: 20 },
      // 当前用户权限标记（由列表接口返回，控制筛选区显隐；默认不受限）
      myRights: { isSelfOnly: false },
      // 表格固定高度（撑满内容区剩余空间，滚动收进表格内部）
      tableHeight: null,
      list: [],
      total: 0,
      loading: false,
      employeeList: [],
      // 添加弹窗
      addDialog: { visible: false, projectLoading: false, submitting: false },
      addForm: this.getEmptyAddForm(),
      projectOptions: [],
      // 修改弹窗
      editDialog: { visible: false, submitting: false },
      editForm: this.getEmptyEditForm(),
      // 管理员批量删除（临时功能）
      isAdmin: false,
      multipleSelection: [],
      batchDeleteLoading: false,
      // 状态修改弹窗（pendingPlatforms/platformOpenDates 为翻新中"设置开业"专用）
      statusDialog: { visible: false, row: null, status: '', reason: '', submitting: false, pendingPlatforms: [], platformOpenDates: {} },
      // 翻新确认弹窗
      renovateDialog: { visible: false, row: null, generation: 1, reason: '', submitting: false },
      // 永久关闭弹窗（品牌终态：闭店）
      permanentCloseDialog: { visible: false, row: null, reason: '', submitting: false },
      // 指定负责人弹窗
      operatorDialog: { visible: false, row: null, currentName: '-', employeeId: '', reason: '', submitting: false },
      // 详情弹窗（activeGen 为各代 tab 的选中代 storeId）
      detailDialog: { visible: false, activeGen: '' },
      detailData: null,
      // 批量导入
      importDialog: {
        visible: false,
        step: 0,
        transferData: [],
        selectedIds: [],
        downloading: false,
        uploadFile: null,
        uploading: false,
        result: null
      },
      // 企微群绑定/换绑/解绑弹窗
      groupBindDialog: {
        visible: false,
        row: null,
        conversationId: '',
        loading: false,
        submitting: false,
        unbinding: false,
        groupOptions: []
      },
      // 附加信息弹框
      additionalInfoDialog: { visible: false, row: null }
    }
  },
  computed: {
    hasLevel2Platform () {
      return this.addForm.platforms.some(p => p.stage === 'level2')
    },
    editHasLevel2Platform () {
      return this.editForm.platforms.some(p => p.stage === 'level2')
    },
    // 改状态弹窗是否处于"翻新中→设置开业"分支
    isRenovatingStatus () {
      return !!(this.statusDialog.row && this.statusDialog.row.status === '翻新中')
    },
    operatorOptions () {
      return this.employeeList.filter(e => /运营师|运营主管|建店专员/.test(e.job))
    },
    supervisorOptions () {
      return this.employeeList.filter(e => /营运督导/.test(e.job))
    }
  },
  created () {
    // 管理员判断（level<=1 为管理员，可见批量删除临时功能）；level 缺失按普通用户，绝不当成超管
    const level = readLevel()
    this.isAdmin = level <= 1
    this.getList()
    this.getEmployees()
  },
  mounted () {
    window.addEventListener('resize', this.calcTableHeight)
    this.$nextTick(this.calcTableHeight)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.calcTableHeight)
  },
  filters: {
    dateFormat (val) {
      if (!val) return '-'
      return dayjs(val).format('YYYY-MM-DD')
    },
    timeFormat (val) {
      if (!val) return '-'
      return dayjs(val).format('YYYY-MM-DD HH:mm')
    }
  },
  methods: {
    getEmptyAddForm () {
      return {
        projectId: '',
        status: '运营中',
        targetVolume: null,
        platforms: [
          { platform: 'meituan', code: '', storeName: '', openDate: '', stage: 'level1', level2StartedAt: '', targetVolume: '' },
          { platform: 'eleme', code: '', storeName: '', openDate: '', stage: 'level1', level2StartedAt: '', targetVolume: '' },
          { platform: 'jd', code: '', storeName: '', openDate: '', stage: 'level1', level2StartedAt: '', targetVolume: '' }
        ],
        level1OperatorId: '',
        operatingSupervisorId: '',
        level2OperatorId: '',
        currentOperatorId: ''
      }
    },
    getEmptyEditForm () {
      return {
        storeId: '',
        name: '',
        projectName: '',
        status: '',
        generation: 1,
        targetVolume: null,
        platforms: [
          { platform: 'meituan', code: '', storeName: '', openDate: '', stage: 'level1', level2StartedAt: '', targetVolume: '' },
          { platform: 'eleme', code: '', storeName: '', openDate: '', stage: 'level1', level2StartedAt: '', targetVolume: '' },
          { platform: 'jd', code: '', storeName: '', openDate: '', stage: 'level1', level2StartedAt: '', targetVolume: '' }
        ],
        level1OperatorId: '',
        level2OperatorId: '',
        currentOperatorId: '',
        // 翻新执照（翻新中的修改弹窗专用）
        licenseImages: { businessLicense: [], foodPermit: [] }
      }
    },
    platformName (platform) {
      const map = { meituan: '美团', eleme: '饿了么', jd: '京东' }
      return map[platform] || platform
    },
    statusTagType (status) {
      const map = { 运营中: 'success', 待运营: 'warning', 筹建中: 'info', 闭店: 'danger', 解约: 'danger', 关店: 'danger', 停业中: 'warning', 春节停业: 'warning', 翻新中: 'danger', 已翻新: 'info', 服务到期: 'info' }
      return map[status] || ''
    },
    // 已解约/已关店的店铺（isActive:false，生命周期终态）：仅可查看详情，不可再操作
    isTerminatedRow (row) {
      return row && row.isActive === false
    },
    // 代运营门店（列表行为扁平结构：brand = Project.signType）
    isProxyRow (row) {
      return !!(row && row.brand === '代运营')
    },
    // 品牌门店（含手工添加的已关联项目门店；无项目信息的默认不提供品牌专属操作）
    isBrandRow (row) {
      return !!(row && row.brand && row.brand !== '代运营')
    },
    actionLabel (action) {
      const map = { create: '添加', delete: '删除', modifyStatus: '改状态', assignOperator: '指定负责人', modify: '修改', renovate: '翻新', renovationLicense: '翻新执照', permanentClose: '永久关闭' }
      return map[action] || action
    },
    closeReasonLabel (reason) {
      const map = { renovation: '翻新', closure: '关店', termination: '解约', permanentClosure: '永久关闭', expiry: '服务到期', 'renewal-merge': '续签合并' }
      return map[reason] || '-'
    },
    genOperatorsText (assignments) {
      if (!Array.isArray(assignments) || assignments.length === 0) return '-'
      const roleMap = {
        currentOperator: '负责人',
        level1Operator: '一阶',
        level2Operator: '二阶',
        operatingSupervisor: '营运督导',
        constructionSupervisor: '筹建督导'
      }
      return assignments.map(a => `${roleMap[a.role] || a.role}:${a.employeeName}`).join(' ')
    },
    // 运营中且非代运营的品牌门店可发起翻新（后端还会校验操作人权限）
    canRenovate (row) {
      return row.status === '运营中' && row.brand !== '代运营'
    },
    currentOperatorName (assignments) {
      if (!Array.isArray(assignments)) return '-'
      const cur = assignments.find(a => a.role === 'currentOperator')
      return cur ? cur.employeeName : '-'
    },
    operatorName (assignments, role) {
      if (!Array.isArray(assignments)) return '-'
      const cur = assignments.find(a => a.role === role)
      return cur ? cur.employeeName : '-'
    },

    // ===== 列表 =====
    async getList (first = false) {
      if (first) this.queryInfo.pagenum = 1
      this.loading = true
      try {
        const { data: res } = await this.$http.get('yunying/dianpu/list', { params: this.queryInfo })
        if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
        this.list = res.data.list
        this.total = res.data.total
        this.myRights = res.data.myRights || { isSelfOnly: false }
      } finally {
        this.loading = false
        // 权限返回后筛选区（运营师/现阶段负责人）可能显隐变化，重算表格高度
        this.$nextTick(this.calcTableHeight)
      }
    },
    // 表格高度 = 内容区滚动容器高度 - 表格顶部偏移 - 分页区高度（滚动收进表格内部，横向滚动条常驻表格底部）
    calcTableHeight () {
      const container = this.$el.closest('.content')
      const tableEl = this.$el.querySelector('.el-table')
      const paginationEl = this.$el.querySelector('.el-pagination')
      if (!tableEl) return
      let containerTop = 0
      let containerHeight = window.innerHeight
      if (container) {
        const rect = container.getBoundingClientRect()
        containerTop = rect.top
        containerHeight = rect.height
      }
      const tableTop = tableEl.getBoundingClientRect().top - containerTop
      // 余量含分页 margin/高度与卡片内边距、边框（实测合计约 56px，保证分页完整可见、页面无纵向溢出）
      const paginationH = paginationEl ? paginationEl.offsetHeight + 56 : 56
      this.tableHeight = Math.max(300, containerHeight - tableTop - paginationH)
    },
    resetQuery () {
      this.queryInfo = { keyword: '', status: '运营中', operatorId: '', currentOperatorId: '', groupBound: '', brand: '', pagenum: 1, pagesize: 20 }
      this.getList()
    },
    handleSizeChange (size) { this.queryInfo.pagesize = size; this.getList() },
    handleCurrentChange (page) { this.queryInfo.pagenum = page; this.getList() },

    // ===== 员工列表 =====
    async getEmployees () {
      const { data: res } = await this.$http.get('yunying/employees/list')
      if (res.meta.status !== 200) return
      this.employeeList = res.data || []
    },

    // ===== 添加 =====
    openAddDialog () {
      this.addForm = this.getEmptyAddForm()
      this.projectOptions = []
      this.addDialog.visible = true
    },
    async searchProjects (keyword) {
      this.addDialog.projectLoading = true
      try {
        const { data: res } = await this.$http.get('yunying/dianpu/search-projects', { params: { keyword } })
        if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
        this.projectOptions = res.data.list || []
      } finally { this.addDialog.projectLoading = false }
    },
    onProjectChange (projectId) {
      // 可选：选中项目后预填门店名
      const project = this.projectOptions.find(p => p._id === projectId)
      if (project) {
        // addForm.name 不单独存，后端用项目名
      }
    },
    onStageChange (index) {
      // 切到 level1 时清空 level2StartedAt
      if (this.addForm.platforms[index].stage === 'level1') {
        this.addForm.platforms[index].level2StartedAt = ''
        // 如果没有任何 level2 平台了，清空 level2OperatorId
        if (!this.hasLevel2Platform) {
          this.addForm.level2OperatorId = ''
        }
      }
    },
    onEditStageChange (index) {
      // 切到 level1 时清空 level2StartedAt（修改弹框版）
      if (this.editForm.platforms[index].stage === 'level1') {
        this.editForm.platforms[index].level2StartedAt = ''
        if (!this.editHasLevel2Platform) {
          this.editForm.level2OperatorId = ''
        }
      }
    },
    async confirmAdd () {
      // 前端校验
      if (!this.addForm.projectId) return this.$message.warning('请选择项目')
      if (!this.addForm.status) return this.$message.warning('请选择门店状态')
      if (this.addForm.targetVolume == null) return this.$message.warning('请填写店铺单量')
      // stage=level2 必须有 level2StartedAt
      for (const p of this.addForm.platforms) {
        if (p.code && p.stage === 'level2' && !p.level2StartedAt) {
          return this.$message.warning(`${this.platformName(p.platform)}为二阶，请填写转二阶日期`)
        }
      }
      // 有 level2 平台必须有 level2OperatorId
      if (this.hasLevel2Platform && !this.addForm.level2OperatorId) {
        return this.$message.warning('存在二阶平台，必须选择二阶运营师')
      }
      // 全部 level1 不能选二阶运营师
      if (!this.hasLevel2Platform && this.addForm.level2OperatorId) {
        return this.$message.warning('所有平台均为一阶，不能选择二阶运营师')
      }

      // 组装数据
      const platforms = this.addForm.platforms.filter(p => p.code).map(p => ({
        platform: p.platform,
        code: p.code,
        storeName: p.storeName,
        openDate: p.openDate || null,
        stage: p.stage,
        level2StartedAt: p.level2StartedAt || null,
        targetVolume: p.targetVolume ? Number(p.targetVolume) : null
      }))
      // 至少需要一个平台
      if (platforms.length === 0) return this.$message.warning('请至少填写一个平台的编码')

      // 组装负责人姓名（从 employeeList 查）
      const findName = (id) => {
        const emp = this.employeeList.find(e => e._id === id)
        return emp ? emp.name : ''
      }

      const body = {
        projectId: this.addForm.projectId,
        status: this.addForm.status,
        targetVolume: this.addForm.targetVolume,
        platforms,
        level1OperatorId: this.addForm.level1OperatorId,
        level1OperatorName: findName(this.addForm.level1OperatorId),
        operatingSupervisorId: this.addForm.operatingSupervisorId,
        operatingSupervisorName: findName(this.addForm.operatingSupervisorId),
        level2OperatorId: this.addForm.level2OperatorId,
        level2OperatorName: findName(this.addForm.level2OperatorId),
        currentOperatorId: this.addForm.currentOperatorId,
        currentOperatorName: findName(this.addForm.currentOperatorId)
      }

      this.addDialog.submitting = true
      try {
        const { data: res } = await this.$http.post('yunying/dianpu/add', body)
        if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
        this.$message.success('添加成功')
        this.addDialog.visible = false
        this.getList()
      } finally { this.addDialog.submitting = false }
    },

    // ===== 修改 =====
    async openEditDialog (row) {
      const { data: res } = await this.$http.get(`yunying/dianpu/detail/${row._id}`)
      if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
      const store = res.data.store

      // 回填基础字段
      this.editForm.storeId = store._id
      this.editForm.name = store.name || ''
      this.editForm.projectName = store.projectName || ''
      this.editForm.status = store.status
      this.editForm.generation = store.generation || 1
      this.editForm.targetVolume = store.targetVolume

      // 回填平台：按 platform 字段映射到 3 个固定槽（有数据回填、无则空槽）
      const platformMap = {}
      for (const p of (store.platforms || [])) {
        platformMap[p.platform] = p
      }
      const platformOrder = ['meituan', 'eleme', 'jd']
      this.editForm.platforms = platformOrder.map(pf => {
        const p = platformMap[pf]
        if (p) {
          return {
            platform: pf,
            code: p.code || '',
            storeName: p.storeName || '',
            openDate: p.openDate ? this.$getDate(p.openDate) : '',
            stage: p.stage || 'level1',
            level2StartedAt: p.level2StartedAt ? this.$getDate(p.level2StartedAt) : '',
            targetVolume: p.targetVolume != null ? p.targetVolume : ''
          }
        }
        return { platform: pf, code: '', storeName: '', openDate: '', stage: 'level1', level2StartedAt: '', targetVolume: '' }
      })

      // 回填运营师（按 role 取）
      const assignments = Array.isArray(store.assignments) ? store.assignments : []
      this.editForm.level1OperatorId = this._pickAssignmentId(assignments, 'level1Operator')
      this.editForm.level2OperatorId = this._pickAssignmentId(assignments, 'level2Operator')
      this.editForm.currentOperatorId = this._pickAssignmentId(assignments, 'currentOperator')

      // 回填翻新执照（翻新中专用区域）
      const licenses = Array.isArray(store.renovationLicenses) ? store.renovationLicenses : []
      this.editForm.licenseImages = {
        businessLicense: licenses.filter(l => l.type === 'businessLicense').map(l => l.fileUrl),
        foodPermit: licenses.filter(l => l.type === 'foodPermit').map(l => l.fileUrl)
      }

      this.editDialog.visible = true
    },
    _pickAssignmentId (assignments, role) {
      const a = assignments.find(x => x.role === role)
      return a ? a.employeeId : ''
    },
    async confirmEdit () {
      // 前端校验
      if (!this.editForm.name || !this.editForm.name.trim()) return this.$message.warning('请填写门店名称')
      if (this.editForm.targetVolume == null) return this.$message.warning('请填写店铺单量')
      // stage=level2 必须有 level2StartedAt
      for (const p of this.editForm.platforms) {
        if (p.code && p.stage === 'level2' && !p.level2StartedAt) {
          return this.$message.warning(`${this.platformName(p.platform)}为二阶，请填写转二阶日期`)
        }
      }
      if (this.editHasLevel2Platform && !this.editForm.level2OperatorId) {
        return this.$message.warning('存在二阶平台，必须选择二阶运营师')
      }
      if (!this.editHasLevel2Platform && this.editForm.level2OperatorId) {
        return this.$message.warning('所有平台均为一阶，不能选择二阶运营师')
      }

      const platforms = this.editForm.platforms.filter(p => p.code).map(p => ({
        platform: p.platform,
        code: p.code,
        storeName: p.storeName,
        openDate: p.openDate || null,
        stage: p.stage,
        level2StartedAt: p.level2StartedAt || null,
        targetVolume: p.targetVolume !== '' ? Number(p.targetVolume) : null
      }))
      if (platforms.length === 0) return this.$message.warning('至少保留一个平台')

      // 二次确认
      try {
        await this.$confirm(
          `确认修改店铺"${this.editForm.projectName || ''}"的信息？此操作将更新店铺数据，不可撤销。`,
          '修改确认',
          { confirmButtonText: '确认修改', cancelButtonText: '取消', type: 'warning' }
        )
      } catch { return }

      const findName = (id) => {
        const emp = this.employeeList.find(e => e._id === id)
        return emp ? emp.name : ''
      }

      const body = {
        name: this.editForm.name.trim(),
        targetVolume: this.editForm.targetVolume,
        platforms,
        level1OperatorId: this.editForm.level1OperatorId,
        level1OperatorName: findName(this.editForm.level1OperatorId),
        level2OperatorId: this.editForm.level2OperatorId,
        level2OperatorName: findName(this.editForm.level2OperatorId),
        currentOperatorId: this.editForm.currentOperatorId,
        currentOperatorName: findName(this.editForm.currentOperatorId)
      }

      this.editDialog.submitting = true
      try {
        // 翻新产生的代（翻新中或渐进上新中的运营中）：先保存执照留存
        if (this.editForm.status === '翻新中' || this.editForm.generation > 1) {
          const licenses = [
            ...this.editForm.licenseImages.businessLicense.map(url => ({ type: 'businessLicense', fileUrl: url, fileName: '' })),
            ...this.editForm.licenseImages.foodPermit.map(url => ({ type: 'foodPermit', fileUrl: url, fileName: '' }))
          ]
          const { data: lres } = await this.$http.put(`yunying/dianpu/renovation-license/${this.editForm.storeId}`, { licenses })
          if (lres.meta.status !== 200) return this.$message.error(lres.meta.msg)
        }

        const { data: res } = await this.$http.put(`yunying/dianpu/modify/${this.editForm.storeId}`, body)
        if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
        this.$message.success('修改成功')
        this.editDialog.visible = false
        this.getList()
      } finally { this.editDialog.submitting = false }
    },

    // ===== 修改状态 / 设置开业 =====
    async openStatusDialog (row) {
      this.statusDialog.row = row
      this.statusDialog.status = row.status
      this.statusDialog.reason = ''
      this.statusDialog.pendingPlatforms = []
      this.statusDialog.platformOpenDates = {}
      if (row.status === '翻新中') {
        // 设置开业：拉取平台信息，列出尚未填写开业时间的平台
        this.statusDialog.status = '运营中'
        const { data: res } = await this.$http.get(`yunying/dianpu/detail/${row._id}`)
        if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
        this.statusDialog.pendingPlatforms = (res.data.store.platforms || []).filter(p => p.status === 1 && !p.openDate)
      }
      this.statusDialog.visible = true
    },
    async confirmStatus () {
      if (!this.statusDialog.status) return this.$message.warning('请选择状态')
      if (!this.statusDialog.reason.trim()) return this.$message.warning('请填写修改理由')
      const body = { status: this.statusDialog.status, reason: this.statusDialog.reason }
      if (this.isRenovatingStatus) {
        for (const p of this.statusDialog.pendingPlatforms) {
          if (!this.statusDialog.platformOpenDates[p.platform]) {
            return this.$message.warning(`请填写${this.platformName(p.platform)}的开业时间`)
          }
        }
        body.platformOpenDates = this.statusDialog.platformOpenDates
      }
      this.statusDialog.submitting = true
      try {
        const { data: res } = await this.$http.put(`yunying/dianpu/modify-status/${this.statusDialog.row._id}`, body)
        if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
        this.$message.success(this.isRenovatingStatus ? '设置开业成功，门店已恢复运营中' : '修改成功')
        this.statusDialog.visible = false
        this.getList()
      } finally { this.statusDialog.submitting = false }
    },

    // ===== 永久关闭（品牌终态：闭店） =====
    openPermanentCloseDialog (row) {
      this.permanentCloseDialog.row = row
      this.permanentCloseDialog.reason = ''
      this.permanentCloseDialog.visible = true
    },
    async confirmPermanentClose () {
      if (!this.permanentCloseDialog.reason.trim()) return this.$message.warning('请填写关闭原因')
      this.permanentCloseDialog.submitting = true
      try {
        const { data: res } = await this.$http.put(`yunying/dianpu/permanent-close/${this.permanentCloseDialog.row._id}`, {
          reason: this.permanentCloseDialog.reason
        })
        if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
        this.$message.success('已永久关闭')
        this.permanentCloseDialog.visible = false
        this.getList()
      } finally { this.permanentCloseDialog.submitting = false }
    },

    // ===== 翻新 =====
    openRenovateDialog (row) {
      this.renovateDialog.row = row
      this.renovateDialog.generation = row.generation || 1
      this.renovateDialog.reason = ''
      this.renovateDialog.visible = true
    },
    async confirmRenovate () {
      this.renovateDialog.submitting = true
      try {
        const { data: res } = await this.$http.put(`yunying/dianpu/renovate/${this.renovateDialog.row._id}`, {
          reason: this.renovateDialog.reason
        })
        if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
        this.$message.success('翻新成功，门店进入"翻新中"，请上传执照并完成换号')
        this.renovateDialog.visible = false
        this.getList()
      } finally { this.renovateDialog.submitting = false }
    },

    // ===== 指定现阶段负责人 =====
    openOperatorDialog (row) {
      this.operatorDialog.row = row
      const cur = row.assignments && row.assignments.currentOperator
      this.operatorDialog.currentName = cur ? cur.employeeName : '无'
      this.operatorDialog.employeeId = ''
      this.operatorDialog.reason = ''
      this.operatorDialog.visible = true
    },
    async confirmOperator () {
      if (!this.operatorDialog.employeeId) return this.$message.warning('请选择新负责人')
      if (!this.operatorDialog.reason.trim()) return this.$message.warning('请填写理由')
      const emp = this.operatorOptions.find(e => e._id === this.operatorDialog.employeeId)
      this.operatorDialog.submitting = true
      try {
        const { data: res } = await this.$http.put(`yunying/dianpu/assign-operator/${this.operatorDialog.row._id}`, {
          employeeId: this.operatorDialog.employeeId,
          employeeName: emp ? emp.name : '',
          reason: this.operatorDialog.reason
        })
        if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
        this.$message.success('指定成功')
        this.operatorDialog.visible = false
        this.getList()
      } finally { this.operatorDialog.submitting = false }
    },

    // ===== 删除 =====
    async handleRemove (row) {
      try {
        await this.$confirm(`确认删除店铺"${row.name}"？此操作不可恢复。`, '删除确认', { type: 'warning' })
      } catch (e) { return }
      const { data: res } = await this.$http.delete(`yunying/dianpu/remove/${row._id}`)
      if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
      this.$message.success('删除成功')
      this.getList()
    },

    // ===== 批量删除（管理员临时功能） =====
    handleSelectionChange (val) {
      this.multipleSelection = val
    },
    async batchDelete () {
      const count = this.multipleSelection.length
      if (count === 0) return
      try {
        await this.$confirm(
          `确认批量删除选中的 ${count} 家店铺？此操作不可恢复。\n（有筹建记录的店铺会被服务端拒绝，仅删除允许删除的）`,
          '批量删除确认', { type: 'warning', confirmButtonText: '确认删除', cancelButtonText: '取消' }
        )
      } catch (e) { return }

      this.batchDeleteLoading = true
      let success = 0
      const fails = []
      for (const row of this.multipleSelection) {
        try {
          const { data: res } = await this.$http.delete(`yunying/dianpu/remove/${row._id}`)
          if (res.meta.status === 200) {
            success++
          } else {
            fails.push(`${row.name}: ${res.meta.msg}`)
          }
        } catch (e) {
          fails.push(`${row.name}: ${e.message}`)
        }
      }
      this.batchDeleteLoading = false
      this.multipleSelection = []

      let msg = `批量删除完成：成功 ${success} 家`
      if (fails.length > 0) {
        msg += `，失败 ${fails.length} 家`
        this.$notify({ title: '部分删除失败', message: fails.join('\n'), type: 'warning', duration: 0 })
      }
      this.$message.success(msg)
      this.getList()
    },

    // ===== 详情 =====
    async openDetail (row) {
      const { data: res } = await this.$http.get(`yunying/dianpu/detail/${row._id}`)
      if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
      this.detailData = res.data.store
      this.detailDialog.activeGen = res.data.store._id
      this.detailDialog.visible = true
    },

    // ===== 批量导入 =====
    openImportDialog () {
      this.importDialog.step = 0
      this.importDialog.transferData = []
      this.importDialog.selectedIds = []
      this.importDialog.uploadFile = null
      this.importDialog.result = null
      this.importDialog.visible = true
      this.loadImportProjects()
    },
    async loadImportProjects () {
      const { data: res } = await this.$http.get('yunying/dianpu/search-projects', { params: { keyword: '' } })
      if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
      this.importDialog.transferData = (res.data.list || []).map(p => ({
        _id: p._id,
        label: p.projectName
      }))
    },
    downloadTemplate () {
      if (this.importDialog.selectedIds.length === 0) {
        return this.$message.warning('请至少选择一个项目')
      }
      this.importDialog.step = 1
    },
    async doDownloadTemplate () {
      this.importDialog.downloading = true
      try {
        const projectIds = [...this.importDialog.selectedIds]
        const res = await this.$http.post('yunying/dianpu/import-template', { projectIds }, { responseType: 'blob' })
        // 检查是否是错误响应（json）
        if (res.data.type === 'application/json') {
          const text = await res.data.text()
          const errData = JSON.parse(text)
          return this.$message.error(errData.meta?.msg || '下载失败')
        }
        const url = window.URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', '店铺导入模板.xlsx')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        this.$message.success('模板已下载')
      } catch (error) {
        this.$message.error('下载失败')
      } finally {
        this.importDialog.downloading = false
      }
    },
    handleFileChange (file) {
      this.importDialog.uploadFile = file.raw
    },
    handleFileRemove () {
      this.importDialog.uploadFile = null
    },
    async doUpload () {
      if (!this.importDialog.uploadFile) return this.$message.warning('请选择文件')
      this.importDialog.uploading = true
      try {
        const formData = new FormData()
        formData.append('file', this.importDialog.uploadFile)
        const { data: res } = await this.$http.post('yunying/dianpu/import', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
        this.importDialog.result = res.data
        this.importDialog.step = 3
        this.getList()
      } catch (error) {
        this.$message.error('导入失败')
      } finally {
        this.importDialog.uploading = false
      }
    },

    // ===== 企微群绑定/换绑/解绑 =====
    openGroupBindDialog (row) {
      this.groupBindDialog.row = row
      this.groupBindDialog.conversationId = ''
      this.groupBindDialog.visible = true
      // 预加载群列表
      this.searchGroups('')
    },
    async searchGroups (keyword) {
      this.groupBindDialog.loading = true
      try {
        const { data: res } = await this.$http.get('yunying/dianpu/search-groups', { params: { keyword } })
        if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
        this.groupBindDialog.groupOptions = res.data.list || []
      } catch (e) {
        this.$message.error('搜索群失败')
      } finally {
        this.groupBindDialog.loading = false
      }
    },
    async confirmGroupBind () {
      if (!this.groupBindDialog.conversationId) {
        return this.$message.warning('请选择群')
      }
      const isRebind = this.groupBindDialog.row && this.groupBindDialog.row.groupBound
      // 换绑二次确认
      if (isRebind) {
        try {
          await this.$confirm(
            `确认换绑项目"${this.groupBindDialog.row.projectName}"的企微群？原群将被替换。`,
            '换绑确认',
            { type: 'warning', confirmButtonText: '确认换绑', cancelButtonText: '取消' }
          )
        } catch { return }
      }
      this.groupBindDialog.submitting = true
      try {
        const { data: res } = await this.$http.post('yunying/dianpu/group-bind', {
          storeId: this.groupBindDialog.row._id,
          conversationId: this.groupBindDialog.conversationId
        })
        if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
        this.$message.success(isRebind ? '换绑成功' : '绑定成功')
        this.groupBindDialog.visible = false
        this.getList()
      } catch (e) {
        this.$message.error('操作失败')
      } finally {
        this.groupBindDialog.submitting = false
      }
    },
    async confirmGroupUnbind () {
      try {
        await this.$confirm(
          `确认解除项目"${this.groupBindDialog.row.projectName}"的企微群绑定？`,
          '解绑确认',
          { type: 'warning', confirmButtonText: '确认解绑', cancelButtonText: '取消' }
        )
      } catch { return }
      this.groupBindDialog.unbinding = true
      try {
        const { data: res } = await this.$http.delete(`yunying/dianpu/group-unbind/${this.groupBindDialog.row._id}`)
        if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
        this.$message.success('解绑成功')
        this.groupBindDialog.visible = false
        this.getList()
      } catch (e) {
        this.$message.error('操作失败')
      } finally {
        this.groupBindDialog.unbinding = false
      }
    },
    resetGroupBindDialog () {
      this.groupBindDialog.row = null
      this.groupBindDialog.conversationId = ''
      this.groupBindDialog.groupOptions = []
    },

    // ===== 附加信息（项目级键值对；hover 预览 + 点击弹框看全量） =====
    hasAdditionalInfo (info) {
      return Object.values(info || {}).some(v => v !== null && v !== undefined && String(v).trim() !== '')
    },
    // 预览条目：只取非空字段，多行压成一行并截断长值
    additionalInfoPreview (info) {
      return Object.entries(info || {})
        .filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '')
        .map(([k, v]) => {
          const text = String(v).replace(/\s+/g, ' ').trim()
          return { key: k, value: text.length > 40 ? text.slice(0, 40) + '…' : text }
        })
    },
    openAdditionalInfoDialog (row) {
      this.additionalInfoDialog.row = row
      this.additionalInfoDialog.visible = true
    },
    getMultLine (value) {
      if (value === null || value === undefined || value === '') return '--'
      // 先转义再换行：值来自表单录入，防止 v-html 渲染注入的 HTML
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
        .split('\n')
        .join('<br>')
    }
  }
}
</script>

<style lang="less" scoped>
.search-form {
  margin-bottom: 15px;
}
.platform-icon {
  width: 20px;
  height: 20px;
  vertical-align: middle;
}
.wework-icon {
  width: 20px;
  height: 20px;
  vertical-align: middle;
  cursor: pointer;
}
.wework-icon-unbound {
  filter: grayscale(1);
  opacity: 0.4;
}
/* 附加信息 hover 预览：紧凑键值行，超高滚动（tooltip 默认可移入） */
.addinfo-preview {
  min-width: 180px;
  max-height: 300px;
  overflow-y: auto;
  font-size: 12px;
  line-height: 1.9;
}
/* 附加信息弹框头部：项目名 + 门店名 */
.addinfo-dialog-head {
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 600;
  .addinfo-dialog-sub {
    margin-left: 8px;
    font-size: 12px;
    font-weight: normal;
    color: #909399;
  }
}
/* 翻新执照上传区：缩小 picture-card 尺寸（Element 默认 148px），降低弹窗高度 */
.license-upload-row /deep/ .el-upload--picture-card {
  width: 80px;
  height: 80px;
  line-height: 78px;
}
.license-upload-row /deep/ .el-upload-list--picture-card .el-upload-list__item {
  width: 80px;
  height: 80px;
}
/deep/ .import-transfer .el-transfer-panel {
  width: 300px;
  .el-transfer-panel__body {
    height: 450px;
  }
  .el-transfer-panel__list {
    height: 380px;
  }
}
</style>
